import {
  Button,
  ConfirmationModal,
  ContextualBanner,
  Dropdown,
  DropdownOption,
  Input,
  Stack,
  TabbedInterface,
  TabList,
  TabPanel,
  TabPanelItem,
  Toggle,
} from '@hawkins/components';
import { useEffect, useState } from 'react';
import styles from './CalculatorInput.module.css';

import { alignmentLookup } from 'src/domains/api/static';
import { Grid } from 'src/domains/shared/components/Grid';

import { Dimensions } from '@hawkins/variables';
import { CalculatorInputProps, FormState, SqueezedMode } from '../../types';
import {
  createResolutionFromValues,
  createResolutionInputFromValues,
} from '../utilities/createResolution';
import { AspectRatioDropdown } from './AspectRatioDropdown';
import { CameraAndSensorModeInput } from './CameraAndSensorModeInput';
import { EditableTab } from './EditableTab';
import { HorizontalInputStack } from './HorizontalInputStack';
import { InputSectionHeader } from './InputSectionHeader';
import { LensSqueezeInput } from './LensSqueezeInput';
import {
  applyLensSqueezeSelection,
  LENS_SQUEEZE_CHANGE_CONFIRM_MESSAGE,
  LENS_SQUEEZE_CHANGE_CONFIRM_TITLE,
  shouldPromptForLensSqueezeChange,
} from './lensSqueezeChangeHandler';
import { ProtectionInput } from './ProtectionInput';
import { FieldModes } from './types';
import { CustomFramingDecisionInput } from './CustomFramingDecisionInput';
import { ResolutionInput } from '../FramingChart/types';
import { EffectiveCanvasInput } from './EffectiveCanvasInput';

export const CalculatorInput = ({
  calculatedInputValues,
  defaultFormValues,
  form,
  canvasResolution,
  effectiveCanvas,
}: CalculatorInputProps) => {
  const effectiveCanvasResolutionInput = createResolutionInputFromValues(
    form.values.effectiveCanvasWidth,
    form.values.effectiveCanvasHeight
  );

  const customFramingDecisionInput = createResolutionInputFromValues(
    form.values.customFramingDecisionWidth,
    form.values.customFramingDecisionHeight
  );

  const validCustomFramingDecision = createResolutionFromValues(
    form.values.customFramingDecisionWidth,
    form.values.customFramingDecisionHeight
  );

  const isAspectRatioLocked = form.values.lockAspectRatio;

  const fieldModes: FieldModes =
    validCustomFramingDecision != null
      ? {
          aspectRatio: isAspectRatioLocked ? 'input' : 'output',
          protection: 'output',
          framingDecision: 'input',
        }
      : {
          aspectRatio: 'input',
          protection: 'input',
          framingDecision: 'output',
        };

  const [selectedFramingDecision, setSelectedFramingDecision] =
    useState<string>('0');

  const [pendingLensSqueeze, setPendingLensSqueeze] =
    useState<DropdownOption<number> | null>(null);

  // Auto-update protection when calculated from custom framing or corrected from rounding
  useEffect(() => {
    if (calculatedInputValues && calculatedInputValues.protection !== null) {
      const shouldUpdateProtection =
        // Update when protection is in output mode (custom framing)
        fieldModes.protection === 'output' ||
        // Update when protection needs correction due to rounding (non-custom framing)
        (fieldModes.protection === 'input' &&
          form.values.safetyCrop !== calculatedInputValues.protection);

      if (
        shouldUpdateProtection &&
        form.values.safetyCrop !== calculatedInputValues.protection
      ) {
        form.setFieldValue('safetyCrop', calculatedInputValues.protection);
      }
    }
  }, [form, fieldModes.protection, calculatedInputValues]);

  return (
    <form onSubmit={form.handleSubmit}>
      <Grid gap={Dimensions.Space16} columns="minmax(0, 800px)">
        <InputSectionHeader
          headerText="Step 1 - Define Camera Format"
          tooltipText="Choose a camera and sensor mode to get started"
        />
        <Stack
          className={styles.calculatorInputSection}
          gap={Dimensions.Space16}
        >
          <CameraAndSensorModeInput
            sensorMode={form.values.sensorMode}
            onSensorModeChange={(selected) => {
              form.setFieldValue('sensorMode', selected);
            }}
          />

          {form.values.sensorMode?.value && (
            <EffectiveCanvasInput
              canvasResolution={canvasResolution ?? undefined}
              effectiveCanvas={effectiveCanvasResolutionInput}
              setEffectiveCanvas={({ width, height }: ResolutionInput) => {
                form.setFieldValue('effectiveCanvasWidth', width);
                form.setFieldValue('effectiveCanvasHeight', height);
              }}
            />
          )}

          <HorizontalInputStack layout={{ gridTemplateColumns: '1fr 1fr' }}>
            <LensSqueezeInput
              onChange={(selected) => {
                if (!selected?.value || isNaN(selected.value)) {
                  return;
                }
                if (shouldPromptForLensSqueezeChange(selected, form.values)) {
                  setPendingLensSqueeze(selected);
                  return;
                }
                applyLensSqueezeSelection({ selected, form });
              }}
              value={form.values.lensSqueezeFactor}
            />
            {form.values.lensSqueezeFactor?.value !== 1 && (
              // Desqueezed previews are only relevant if there is an anamorphic lens squeeze factor
              <Toggle
                label="Enable De-Squeezed Preview"
                className={styles.desqueezedPreviewToggle}
                onChange={(_event, checked) => {
                  const squeezedMode = checked
                    ? SqueezedMode.Desqueezed
                    : SqueezedMode.Squeezed;
                  form.setFieldValue('squeezedMode', squeezedMode);
                }}
                checked={form.values.squeezedMode === SqueezedMode.Desqueezed}
              />
            )}
          </HorizontalInputStack>
        </Stack>

        <TabbedInterface>
          <InputSectionHeader
            headerText="Step 2 - Add Framing Decisions"
            tooltipText="Calculated values will be rounded to the nearest even number"
          />

          <TabList
            onChange={(newValue) => {
              setSelectedFramingDecision(newValue);
            }}
            value={selectedFramingDecision}
          >
            <EditableTab
              title={form.values.framingIntentLabel || 'Framing Decision 1'}
              onTitleChange={(newTitle) => {
                form.setFieldValue('framingIntentLabel', newTitle);
              }}
            />
          </TabList>

          <TabPanel className={styles.tabPanel}>
            <TabPanelItem>
              <Stack
                className={styles.calculatorInputSection}
                gap={Dimensions.Space16}
              >
                <ContextualBanner
                  className={styles.aspectRatioContextualBanner}
                  style={{
                    display:
                      fieldModes.aspectRatio === 'output' ||
                      form.values.lockAspectRatio ||
                      fieldModes.protection === 'output'
                        ? 'block'
                        : 'none',
                  }}
                  compact
                  text={(() => {
                    const aspectRatioCalculated =
                      fieldModes.aspectRatio === 'output' &&
                      !form.values.lockAspectRatio;
                    const protectionCalculated =
                      fieldModes.protection === 'output';

                    if (form.values.lockAspectRatio) {
                      return protectionCalculated
                        ? 'Aspect ratio is locked and will not change. Protection is being calculated from custom framing decision values.'
                        : 'Aspect ratio is locked and will not change.';
                    }

                    if (aspectRatioCalculated && protectionCalculated) {
                      return 'Aspect ratio and protection are being calculated from the custom framing decision values.';
                    }

                    if (aspectRatioCalculated) {
                      return 'Aspect ratio is being calculated from custom framing decision values.';
                    }

                    if (protectionCalculated) {
                      return 'Protection is being calculated from custom framing decision values.';
                    }

                    return '';
                  })()}
                  variant="warning"
                />
                <HorizontalInputStack>
                  <AspectRatioDropdown
                    disabled={
                      fieldModes.aspectRatio === 'output' ||
                      form.values.lockAspectRatio
                    }
                    value={form.values.outputAspectRatio}
                    calculatedValue={calculatedInputValues?.aspectRatio}
                    isCalculated={fieldModes.aspectRatio === 'output'}
                    isLocked={form.values.lockAspectRatio}
                    onChange={(
                      selected: FormState['outputAspectRatio'] | null
                    ) => {
                      form.setFieldValue('outputAspectRatio', selected);
                    }}
                  />

                  <ProtectionInput
                    value={form.values.safetyCrop}
                    onChange={(value) => {
                      form.setFieldValue('safetyCrop', value);
                    }}
                    disabled={fieldModes.protection === 'output'}
                    showRoundingTooltip={true}
                  />
                </HorizontalInputStack>

                {form.values.sensorMode?.value && (
                  <CustomFramingDecisionInput
                    customFramingDecision={customFramingDecisionInput}
                    lensSqueezeFactor={form.values.lensSqueezeFactor.value}
                    lockedAspectRatio={
                      form.values.lockAspectRatio
                        ? form.values.outputAspectRatio.value
                        : undefined
                    }
                    onAspectRatioToggle={(locked) => {
                      form.setFieldValue('lockAspectRatio', locked);
                    }}
                    canvasResolution={canvasResolution ?? undefined}
                    effectiveCanvas={effectiveCanvas ?? undefined}
                    setCustomFramingDecision={(
                      updatedResolution: ResolutionInput
                    ) => {
                      form.setFieldValue(
                        'customFramingDecisionWidth',
                        updatedResolution.width
                      );
                      form.setFieldValue(
                        'customFramingDecisionHeight',
                        updatedResolution.height
                      );
                    }}
                    setLockAspectRatio={(locked: boolean) => {
                      form.setFieldValue('lockAspectRatio', locked);
                    }}
                  />
                )}

                <Dropdown
                  label="Alignment"
                  options={alignmentLookup}
                  value={form.values.alignment}
                  onChange={(selected: FormState['alignment'] | null) => {
                    form.setFieldValue('alignment', selected);
                  }}
                />

                <HorizontalInputStack>
                  <Input
                    label="Show Title"
                    value={form.values.showTitle || ''}
                    onChange={(e) => {
                      form.setFieldValue('showTitle', e.target.value);
                    }}
                  />
                  <Input
                    label="Director of Photography"
                    value={form.values.directorOfPhotography || ''}
                    onChange={(e) => {
                      form.setFieldValue(
                        'directorOfPhotography',
                        e.target.value
                      );
                    }}
                  />
                </HorizontalInputStack>
              </Stack>
            </TabPanelItem>
          </TabPanel>
        </TabbedInterface>

        <Button
          variant="secondary-destructive"
          className={styles.resetInputsButton}
          onClick={() =>
            form.resetForm({
              values: defaultFormValues,
            })
          }
        >
          Reset
        </Button>
      </Grid>

      <ConfirmationModal
        open={pendingLensSqueeze !== null}
        title={LENS_SQUEEZE_CHANGE_CONFIRM_TITLE}
        body={LENS_SQUEEZE_CHANGE_CONFIRM_MESSAGE}
        onClose={() => setPendingLensSqueeze(null)}
        onCancel={() => setPendingLensSqueeze(null)}
        onConfirm={() => {
          if (pendingLensSqueeze) {
            applyLensSqueezeSelection({
              selected: pendingLensSqueeze,
              form,
              clearCustomFramingFirst: true,
            });
          }
          setPendingLensSqueeze(null);
        }}
      />
    </form>
  );
};
